import {FC, HTMLAttributes, ReactNode} from 'react';
import useComputedStyles from '@/theme/useComputedStyles';
import { textStyles } from '@/components/ui/typography/typography.styles';

export type TextType = 'main' | 'secondary' | 'error';

export interface TextProps extends HTMLAttributes<HTMLParagraphElement>{
	content?: string;
	type?: 'main' | 'secondary' | 'discrete' | 'error' | 'success' | 'primary' | 'inherit';
	size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
	align?: 'center' | 'left' | 'right';
	lineHeight?: 'normal' | 'relaxed' | 'loose' | 'tight' | 'snug' | 'none';
	invert?: boolean;
	className?: string;
	weight?: 'regular' | 'medium' | 'semibold' | 'bold';
	children?: ReactNode; // Allow children to be passed to the component
	tag?: 'div' | 'p'; // Allow the user to specify the tag type, defaulting to 'p'
}

const Text: FC<TextProps> = ({
	content,
	type = 'main',
	size = 'base',
	align = 'left',
	lineHeight = 'normal',
	weight = 'regular',
	invert = false,
	className = '',
	children,
	tag: Tag = 'p' // Use the tag prop with a default value of 'p'
}) => {
	const textClassnames = useComputedStyles(
		[textStyles],
		{ invert: invert ?? false },
		[type, size, lineHeight, align, weight],
		className
	);
	// Conditionally render content or children inside the specified tag
	const renderText = () => {
		if (content && content.length > 0) {
			return <Tag className={textClassnames}>{content}</Tag>;
		} else if (children) {
			return (
				<Tag className={textClassnames}>
					<>{children}</>
				</Tag>
			);
		}
		return null; // Return null if there's no content or children
	};

	return <>{renderText()}</>;
};

export default Text;
